import { useEffect, useRef, useState } from 'react'
import { Link as ScrollLink } from 'react-scroll'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import Container from '~/components/atoms/Container/Container'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { SkipLinkItem } from '~/services/generateSkipLinks'
import { useMediaQuery } from '~/services/useMediaQuery'

import styles from './ArticleInternalMenu.module.css'

export interface ArticleInternalMenuProps {
  testID?: string
  links: SkipLinkItem[]
  title?: string
  ignoreActive?: boolean
  useLowercase?: boolean
  category?: {
    articleType: string
    articleTypeSlug: string
    articleTaxonomy: string
    articleTypeTitle: string
  }
}

export default function ArticleInternalMenu({
  testID,
  links,
  title,
  category,
  ignoreActive = false,
  useLowercase = false,
}: ArticleInternalMenuProps) {
  const t = useTranslations('articlePage')
  const isMobile = useMediaQuery(`(max-width: 1162px`)

  const ref = useRef<any>(null)
  const sectionsRef = useRef<Array<HTMLElement | null>>([])

  const [activeLink, setActiveLink] = useState(links?.[0]?.link || null)
  const [currentLink, setCurrentLink] = useState(links?.[0] || null)
  const [isOpen, setIsOpen] = useState(false)
  const [flatLinks, setFlatLinks] = useState<
    Array<{ title: string; link: string }>
  >([])

  useEffect(() => {
    // Flatten the links array
    const flattenedLinks = links.flatMap(link => [
      link,
      ...(link.children || []),
    ])
    setFlatLinks(flattenedLinks)
  }, [links])

  useEffect(() => {
    const elements: Array<HTMLElement | null> = []
    flatLinks.forEach(link => {
      const element = document.getElementById(link.link)
      if (element) {
        elements.push(element)
      }
    })

    sectionsRef.current = elements
  }, [flatLinks])

  useEffect(() => {
    const handleScroll = () => {
      if (!ignoreActive) {
        const scrollPosition = window.scrollY
        const sectionOffsets = sectionsRef.current.map(section => {
          return section ? section.offsetTop : 0
        })

        const offsetCalculation = isMobile ? 1500 : 1200

        const currentSectionIndex = sectionOffsets.findIndex(
          (offset, index) =>
            scrollPosition - offsetCalculation >= offset &&
            scrollPosition - offsetCalculation <
              (sectionsRef.current[index + 1]?.offsetTop ?? Infinity)
        )

        if (currentSectionIndex !== -1 && flatLinks[currentSectionIndex]) {
          setActiveLink(flatLinks[currentSectionIndex].link)
          setCurrentLink(flatLinks[currentSectionIndex])
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [flatLinks, ignoreActive, isMobile])

  if (!links || links.length === 0) return null

  const internalContent = () => (
    <>
      <div
        onClick={() => isMobile && setIsOpen(!isOpen)}
        className={styles.header}
      >
        <Text
          size={TextSizes.primaryTitleSml}
          uppercase
          className={classNames(
            styles.topTitle,
            isOpen && isMobile && styles.open
          )}
        >
          {title || t('inThisArticle')}
        </Text>
        {isMobile && (
          <svg
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={classNames(styles.arrowIcon, isOpen && styles.open)}
          >
            <path
              d="M1 1L7 7L13 1"
              stroke="#222222"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      {isMobile && !isOpen && (
        <div className={classNames(styles.menu, styles.menuCurrentLink)}>
          <ScrollLink
            to={currentLink.link}
            href={currentLink.link ? `#${currentLink.link}` : `#`}
            smooth={true}
            className={classNames(styles.menuItem, {
              [styles.lowercase]: useLowercase,
            })}
          >
            <Text size={TextSizes.bodySml}>{currentLink.title}</Text>
          </ScrollLink>
        </div>
      )}
      {(!isMobile || (isMobile && isOpen)) && (
        <div
          className={classNames(styles.menuContainer, isOpen && styles.open)}
        >
          <div className={classNames(styles.menu, isOpen && styles.open)}>
            {links &&
              links.map((link, key) => (
                <div key={`${link.title}-${key}`} className={styles.menuGroup}>
                  <ScrollLink
                    to={link.link}
                    onClick={() => setIsOpen(false)}
                    href={link.link ? `#${link.link}` : `#`}
                    smooth={true}
                    className={classNames(
                      styles.menuItem,
                      activeLink === link.link && styles.active,
                      {
                        [styles.lowercase]: useLowercase,
                      }
                    )}
                  >
                    <Text size={TextSizes.bodySml}>{link.title}</Text>
                  </ScrollLink>
                  {link?.children &&
                    link?.children.map((child, childKey) => {
                      return (
                        <ScrollLink
                          to={child.link}
                          key={`child-link-key-${childKey}-${key}`}
                          href={child.link ? `#${child.link}` : `#`}
                          smooth={true}
                          onClick={() => setIsOpen(false)}
                          className={classNames(
                            styles.menuItem,
                            styles.childMenuItem,
                            activeLink === child.link && styles.active,
                            {
                              [styles.lowercase]: useLowercase,
                            }
                          )}
                        >
                          <Text size={TextSizes.bodySml}>{child.title}</Text>
                        </ScrollLink>
                      )
                    })}
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  )

  return (
    <>
      <div
        className={classNames(styles.base, isOpen && isMobile && styles.open)}
        data-testid={testID}
        ref={ref}
      >
        {isMobile ? (
          <Container>{internalContent()}</Container>
        ) : (
          internalContent()
        )}
      </div>
    </>
  )
}
